import React from 'react';
import { connect as connectR } from 'react-redux';
import { FormControl } from 'react-bootstrap';
import translate from '../translate';
import ValidatorFormGroup from '../main/validator-form-group';
import { RootState } from '../../store';
import { validateEmail } from '../../util/validators';

const VALIDATORS = {
    email: {
        isValid(email) {
            if (!email) {
                return true;
            }
            return validateEmail(email);
        },
    },
};
enum SubscriptionTypes {
    NONE = 'NONE',
    MONTHLY = 'MONTHLY',
    CUMULATIVE_DAILY = 'CUMULATIVE_DAILY',
}

interface UiStrings {
    genericError?: string;
    subscriptionsIntro?: string;
    subscriptionType?: string;
    subscriptionTypes?: string[];
    emailAddress?: string;
    emailAddressError?: string;
}

interface Configuration {
    emailAddress?: string;
    subscription?: SubscriptionTypes;
}
interface HandleChangeInputType {
    _valid: boolean;
    configuration: Configuration;
}
interface Props {
    strings: UiStrings;
    configuration: Configuration;
    disabled?: boolean;
    handleChange: (arg1: HandleChangeInputType) => any;
    error?: boolean;
    partnerManagerDetails: PartnerManagerDetailsType;
}

interface PartnerManagerDetailsType {
    control: string;
}

interface State extends Configuration {
    _dirty: any;
    _valid: boolean;
}

class UsageDataSubscriptionConfigurationForm extends React.Component<
    Props,
    State
> {
    state: State = {
        _dirty: {},
        _valid: true,
    };

    getFieldValue(field) {
        if (!this.props.configuration) {
            return undefined;
        }

        const result = this.props.configuration[field];
        if (result) {
            return result;
        }
        return undefined;
    }

    _shouldHideErrorForField(field) {
        return (
            !this.state._dirty[field] ||
            this._validateField(this.props.configuration, field)
        );
    }

    _validateField(data, field) {
        if (!data) {
            return true;
        }
        if (
            !VALIDATORS[field] ||
            typeof VALIDATORS[field].isValid !== 'function'
        ) {
            return true;
        }
        return VALIDATORS[field].isValid(data[field]);
    }

    _isDataValid(data) {
        return Object.keys(VALIDATORS).reduce((sum, field) => {
            const valid = this._validateField(data, field);
            return sum && valid;
        }, true);
    }

    _handleFieldChange(field, value) {
        const oldData = this.props.configuration;
        const newState = {
            _dirty: {
                ...this.state._dirty,
                [field]: true,
            },
            _valid: this.state._valid,
        };
        const newData = {
            ...oldData,
            [field]: value,
        };
        newState._valid = this._isDataValid(newData);
        this.setState(newState);
        this._handleDataChange(newData, newState._valid);
    }

    _handleDataChange(newData, valid) {
        if (this.props.handleChange) {
            this.props.handleChange({
                _valid: valid,
                configuration: newData,
            });
        }
    }

    maybeRenderError() {
        if (!this.props.error) {
            return null;
        }

        return (
            <div className="alert alert-danger">
                {this.props.strings.genericError}
            </div>
        );
    }

    renderSubscriptionType() {
        const translatedTypes = this.props.strings.subscriptionTypes;

        function renderOption(subscriptionType) {
            return (
                <option key={subscriptionType} value={subscriptionType}>
                    {translatedTypes[subscriptionType]}
                </option>
            );
        }

        return (
            <ValidatorFormGroup
                label={this.props.strings.subscriptionType}
                hideErrorLabel
            >
                <select
                    disabled={this.props.disabled}
                    className={'form-control'}
                    name={'subscription_type'}
                    onChange={(event) => {
                        const target: HTMLSelectElement = event.target;
                        this._handleFieldChange('subscription', target.value);
                    }}
                    value={this.getFieldValue('subscription')}
                    required
                >
                    {Object.values(SubscriptionTypes).map(renderOption)}
                </select>
            </ValidatorFormGroup>
        );
    }

    renderEmailInputField() {
        const shouldRequireEmail =
            this.props.configuration.subscription !== SubscriptionTypes.NONE;
        return (
            <ValidatorFormGroup
                label={this.props.strings.emailAddress}
                hideErrorLabel={this._shouldHideErrorForField('email')}
                errorLabel={this.props.strings.emailAddressError}
            >
                {this.renderStringInput('email', shouldRequireEmail)}
            </ValidatorFormGroup>
        );
    }

    renderStringInput(field, required) {
        return (
            <FormControl
                type={'text'}
                className={'input-text-color'}
                value={this.getFieldValue(field) || ''}
                onChange={(event) => {
                    const target: HTMLInputElement =
                        event.target as HTMLInputElement;
                    this._handleFieldChange(field, target.value);
                }}
                required={required}
                disabled={this.props.disabled}
            />
        );
    }

    render() {
        return (
            <div>
                <p>{this.props.strings.subscriptionsIntro}</p>
                {this.renderSubscriptionType()}
                {this.renderEmailInputField()}
                {this.maybeRenderError()}
            </div>
        );
    }
}

export default connectR((store: RootState) => ({
    partnerManagerDetails: store.partnerManager.partnerManagerDetails,
}))(
    translate('UsageDataSubscriptionConfigurationForm')(
        UsageDataSubscriptionConfigurationForm
    )
);
